import {
  BUDGET_ALERT_LEVELS,
  type BudgetUseView,
  type DigestView,
  digestLines,
  type KeyRotationView,
  keyRotationText,
  type MonitorCheckView,
  type MonitorIncidentView,
  type MonitoringOverview,
  SERVICE_TARGETS,
  type StuckJobView,
  safeHref,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { CircleCheck, CircleDashed, CircleMinus, CircleX, Hourglass, Mail, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';
import { cn } from '../lib/cn';

// Admin → Monitoring (task 45; PLAN §13.2): the synthetic checks the Cron runs every 5 minutes, open and past
// incidents (the ones behind the red admin banner), stuck processing jobs the pipeline-health sweep flagged, AI
// budget use with its 50/80/100% alerts, and the last daily digest. "Run checks now" and "Send digest now" need a
// fresh authenticator check.

export const Route = createFileRoute('/admin/monitoring')({ component: MonitoringScreen });

const TOUR = {
  id: 'admin-monitoring',
  steps: [
    {
      target: '[data-tour="monitoring-checks"]',
      title: 'System checks',
      body: 'Hoopoe checks these every 5 minutes. A check that fails twice in a row opens an incident: a red banner for admins, an email, and a GitHub Issue.',
      learnMore: 'monitoring-and-alerts',
    },
    {
      target: '[data-tour="monitoring-stuck"]',
      title: 'Stuck jobs',
      body: 'A file that stops making progress is restarted once. If it gets stuck again it is stopped and listed here.',
      learnMore: 'monitoring-and-alerts',
    },
    {
      target: '[data-tour="monitoring-budget"]',
      title: 'Budget and digest',
      body: 'Admins are told at 50, 80 and 100% of the monthly AI budget, and get a daily digest in numbers only.',
    },
  ],
};

const when = (ms: number | null) =>
  ms === null ? '—' : new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const usd = (n: number) => `$${n.toFixed(2)}`;

const STATUS = {
  ok: { label: 'Passing', icon: CircleCheck, cls: 'text-success-foreground' },
  failing: { label: 'Failing', icon: CircleX, cls: 'text-danger-foreground' },
  skipped: { label: 'Not checked here', icon: CircleMinus, cls: 'text-muted-foreground' },
  pending: { label: 'Not run yet', icon: CircleDashed, cls: 'text-muted-foreground' },
} as const;

function MonitoringScreen() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'monitoring'],
    queryFn: () => api<MonitoringOverview>('/admin/monitoring'),
    refetchInterval: 60_000,
  });
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong. Try again.' });
  };
  const run = useMutation({
    mutationFn: () => withReauth(() => api<{ overview: MonitoringOverview }>('/admin/monitoring/run', { method: 'POST', body: {} })),
    onSuccess: r => {
      qc.setQueryData(['admin', 'monitoring'], r.overview);
      qc.invalidateQueries({ queryKey: ['monitoring', 'banner'] });
      const failing = r.overview.checks.filter(c => c.status === 'failing').length;
      setMessage({ kind: 'ok', text: failing ? `Checks run: ${failing} failing.` : 'Checks run: all passing.' });
    },
    onError: fail,
  });
  const digest = useMutation({
    mutationFn: () => withReauth(() => api<{ digest: DigestView | null }>('/admin/monitoring/digest', { method: 'POST', body: {} })),
    onSuccess: r => {
      qc.invalidateQueries({ queryKey: ['admin', 'monitoring'] });
      setMessage({ kind: 'ok', text: `Digest sent to ${r.digest?.recipients ?? 0} admin${r.digest?.recipients === 1 ? '' : 's'}.` });
    },
    onError: fail,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Monitoring"
        description="Hoopoe checks its own health every 5 minutes, restarts stuck jobs, and tells the admins when something needs attention."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => digest.mutate()} disabled={digest.isPending}>
              <Send aria-hidden="true" /> {digest.isPending ? 'Sending…' : 'Send digest now'}
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending}>
              <RefreshCw aria-hidden="true" /> {run.isPending ? 'Running…' : 'Run checks now'}
            </Button>
          </div>
        }
      />
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={cn('text-sm', message.kind === 'error' && 'text-danger-foreground')}
        >
          {message.text}
        </p>
      )}
      {q.isError ? (
        <ErrorState error={q.error} title="Monitoring couldn’t be loaded" onRetry={() => q.refetch()} learnMore="monitoring-and-alerts" />
      ) : !q.data ? (
        <div aria-busy="true" className="h-48 animate-pulse rounded-md bg-muted">
          <span className="sr-only">Loading…</span>
        </div>
      ) : (
        <>
          <Checks checks={q.data.checks} channels={q.data.channels} environment={q.data.environment} />
          <Incidents incidents={q.data.incidents} />
          <Stuck jobs={q.data.stuck} />
          <div className="grid gap-6 lg:grid-cols-2" data-tour="monitoring-budget">
            <Budget budget={q.data.budget} />
            <Digest digest={q.data.lastDigest} />
          </div>
          <KeyRotations keys={q.data.keyRotations} />
          <ServiceTargets />
        </>
      )}
    </div>
  );
}

function SectionTitle({ id, title, help }: { id: string; title: string; help: string }) {
  return (
    <div className="flex items-center gap-1">
      <h2 id={id} className="text-base font-semibold">
        {title}
      </h2>
      <FieldHelp label={title} help={help} learnMore="monitoring-and-alerts" />
    </div>
  );
}

function Checks({
  checks,
  channels,
  environment,
}: {
  checks: MonitorCheckView[];
  channels: MonitoringOverview['channels'];
  environment: string;
}) {
  return (
    <section aria-labelledby="monitoring-checks" className="space-y-3" data-tour="monitoring-checks">
      <SectionTitle
        id="monitoring-checks"
        title="System checks"
        help="Run every 5 minutes. A check failing twice in a row opens an incident; the first passing run closes it."
      />
      <p className="text-xs text-muted-foreground" data-testid="monitoring-channels">
        Environment: <span className="font-medium">{environment}</span> · Alerts go to: admin banner, email to admins
        {channels.github ? ', GitHub Issue' : ' (GitHub Issues are not set up here)'}.
      </p>
      <div className="relative overflow-x-auto rounded-md border">
        <table className="w-full text-sm" data-testid="monitoring-checks">
          <thead className="bg-muted text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 font-medium">
                Check
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Status
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Last run
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Last passed
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {checks.map(c => {
              const s = STATUS[c.status];
              return (
                <tr key={c.key} className="border-t" data-check={c.key} data-status={c.status}>
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    <span className="flex items-center gap-1">
                      {c.label}
                      <FieldHelp label={c.label} help={c.help} />
                    </span>
                  </th>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className={cn('flex items-center gap-1.5', s.cls)}>
                      <s.icon className="size-4" aria-hidden="true" />
                      {s.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{when(c.lastRunAt)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{when(c.lastOkAt)}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {c.message ?? (c.latencyMs !== null && c.status === 'ok' ? `${c.latencyMs} ms` : '')}
                    {c.consecutiveFailures > 1 && ` (failed ${c.consecutiveFailures} runs in a row)`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Incidents({ incidents }: { incidents: MonitorIncidentView[] }) {
  return (
    <section aria-labelledby="monitoring-incidents" className="space-y-3">
      <SectionTitle
        id="monitoring-incidents"
        title="Incidents"
        help="A check that kept failing. Open incidents show as a red banner to every admin until the check passes again."
      />
      {incidents.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No incidents" learnMore="monitoring-and-alerts">
          When a check fails twice in a row, the incident is listed here and every admin is told.
        </EmptyState>
      ) : (
        <ul className="divide-y rounded-md border text-sm" data-testid="monitoring-incidents">
          {incidents.map(i => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <span className={cn('font-medium', i.resolvedAt === null && 'text-danger-foreground')}>{i.label}</span>
              <span className="text-muted-foreground">
                {i.resolvedAt === null ? `Open since ${when(i.openedAt)}` : `${when(i.openedAt)} – ${when(i.resolvedAt)} (resolved)`}
              </span>
              {i.message && <span className="text-muted-foreground">· {i.message}</span>}
              {safeHref(i.issueUrl) && (
                <a href={safeHref(i.issueUrl)} target="_blank" rel="noreferrer" className="ms-auto underline">
                  GitHub Issue
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stuck({ jobs }: { jobs: StuckJobView[] }) {
  return (
    <section aria-labelledby="monitoring-stuck" className="space-y-3" data-tour="monitoring-stuck">
      <SectionTitle
        id="monitoring-stuck"
        title="Stuck jobs"
        help="A file with no progress for three times its stage’s usual time is restarted once. Stuck again, it is stopped, marked failed and listed here."
      />
      {jobs.length === 0 ? (
        <EmptyState icon={Hourglass} title="No stuck jobs" learnMore="monitoring-and-alerts">
          Files that got stuck twice are listed here. The uploader can retry them once the cause is fixed.
        </EmptyState>
      ) : (
        <ul className="divide-y rounded-md border text-sm" data-testid="monitoring-stuck">
          {jobs.map(j => (
            <li key={j.documentId} className="flex flex-wrap items-center gap-x-3 px-3 py-2">
              <DataText className="font-medium">{j.filename}</DataText>
              <span className="text-muted-foreground">
                uploaded by <DataText>{j.uploaderName ?? 'unknown'}</DataText> · stopped {when(j.flaggedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Budget({ budget }: { budget: BudgetUseView }) {
  const pct = budget.percent;
  const sent = new Set(budget.alerts.map(a => a.level));
  return (
    <section aria-labelledby="monitoring-budget" className="space-y-3 rounded-lg border bg-background p-4">
      <SectionTitle
        id="monitoring-budget"
        title="AI budget"
        help="This month’s AI spend against the team’s monthly budget (Admin → AI routing). Admins are told at 50, 80 and 100%."
      />
      {budget.capUsd === null ? (
        <p className="text-sm text-muted-foreground">
          No monthly budget is set. Spent this month: <DataNumber>{usd(budget.spentUsd)}</DataNumber>.
        </p>
      ) : (
        <>
          <p className="text-sm">
            <DataNumber>{usd(budget.spentUsd)}</DataNumber> of <DataNumber>{usd(budget.capUsd)}</DataNumber> used in {budget.month} (
            <DataNumber>{pct ?? 0}%</DataNumber>)
          </p>
          <div
            className="relative h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="AI budget used this month"
            aria-valuenow={Math.min(100, Math.round(pct ?? 0))}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={cn('h-full', (pct ?? 0) >= 100 ? 'bg-danger' : (pct ?? 0) >= 80 ? 'bg-warning' : 'bg-primary')}
              style={{ width: `${Math.min(100, pct ?? 0)}%` }}
            />
          </div>
          <ul className="flex flex-wrap gap-2 text-xs" data-testid="budget-levels">
            {BUDGET_ALERT_LEVELS.map(l => (
              <li
                key={l}
                className={cn('rounded-full border px-2 py-0.5', sent.has(l) ? 'border-foreground font-medium' : 'text-muted-foreground')}
              >
                {l}% {sent.has(l) ? '· alert sent' : ''}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** Credentials with an expiry date (task 50; PLAN §13.4 "Rotate keys"): due from 30 days before. Never a value. */
function KeyRotations({ keys }: { keys: KeyRotationView[] }) {
  return (
    <section aria-labelledby="monitoring-keys" className="space-y-3">
      <div className="flex items-center gap-1">
        <h2 id="monitoring-keys" className="text-base font-semibold">
          Keys to rotate
        </h2>
        <FieldHelp
          label="Keys to rotate"
          help="Credentials Hoopoe depends on that expire on a set date. From 30 days before, the key is marked Rotate now and the daily digest reminds every admin."
          learnMore="runbooks"
        />
      </div>
      <ul className="divide-y rounded-md border text-sm" data-testid="monitoring-keys">
        {keys.map(k => (
          <li key={k.key} data-key={k.key} data-due={k.due} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
            <span className="font-medium">{k.label}</span>
            <span className="text-muted-foreground">{k.where}</span>
            <span className={cn('ms-auto flex items-center gap-1.5', k.due ? 'text-danger-foreground' : 'text-muted-foreground')}>
              {k.due ? <CircleX className="size-4" aria-hidden="true" /> : <CircleCheck className="size-4" aria-hidden="true" />}
              {k.due ? `Rotate now: ${keyRotationText(k)}` : `Expires on ${k.expiresOn} (in ${k.daysLeft} days)`}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        <Link to="/onboarding/$slug" params={{ slug: 'runbooks' }} hash="rotate-keys" className="underline underline-offset-2">
          How to rotate a key
        </Link>
      </p>
    </section>
  );
}

/** PLAN §13.3, as the weekly load run on staging checks them (task 47). */
function ServiceTargets() {
  return (
    <section aria-labelledby="monitoring-targets" className="space-y-3">
      <div className="flex items-center gap-1">
        <h2 id="monitoring-targets" className="text-base font-semibold">
          Service targets
        </h2>
        <FieldHelp
          label="Service targets"
          help="How fast Hoopoe should answer at year-1 scale. A load run on staging checks them every week with the real sample set."
          learnMore="service-targets"
        />
      </div>
      <div className="relative overflow-x-auto rounded-md border">
        <table className="w-full text-sm" data-testid="monitoring-targets">
          <thead className="bg-muted text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 font-medium">
                Measure
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Target
              </th>
            </tr>
          </thead>
          <tbody>
            {SERVICE_TARGETS.map(t => (
              <tr key={t.key} className="border-t">
                <td className="px-3 py-2">{t.label}</td>
                <td className="px-3 py-2 whitespace-nowrap">{t.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Results of each run are in the <span className="font-medium">perf</span> workflow on GitHub.{' '}
        <Link to="/onboarding/$slug" params={{ slug: 'service-targets' }} className="underline underline-offset-2">
          How the targets are checked
        </Link>
      </p>
    </section>
  );
}

function Digest({ digest }: { digest: DigestView | null }) {
  return (
    <section aria-labelledby="monitoring-digest" className="space-y-3 rounded-lg border bg-background p-4">
      <SectionTitle
        id="monitoring-digest"
        title="Daily digest"
        help="Every morning (06:43 UTC) each admin gets the last 24 hours in numbers only: no file names, people or data."
      />
      {digest ? (
        <>
          <p className="text-xs text-muted-foreground">
            Last sent {when(digest.sentAt)} to {digest.recipients} admin{digest.recipients === 1 ? '' : 's'}.
          </p>
          <ul className="space-y-0.5 text-sm" data-testid="monitoring-digest">
            {digestLines(digest.summary).map(line => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : (
        <EmptyState icon={Mail} title="No digest sent yet" learnMore="monitoring-and-alerts">
          The first one goes out tomorrow morning, or select Send digest now.
        </EmptyState>
      )}
    </section>
  );
}
